/**
 * Pictures of buildings, for the menu that raises them.
 *
 * The build list was a column of words: name, cost, and two lines of italic
 * flavour under every entry. Ninety pixels each, so nine of the catalogue's
 * forty-odd were on screen at once, and the one thing a player actually wants
 * from that panel — *what am I about to put on my map* — was not on it at all.
 *
 * The pictures already exist. `TextureBank.buildings` bakes every type in the
 * settlement's own vernacular at boot, and has since the day the architecture
 * table was wired up; the build menu simply never asked. This lifts them out of
 * the GPU into data URLs the DOM can show, which is the whole of the file.
 *
 * **It is a readback, so it is cached hard.** `extract.canvas` pulls pixels back
 * off the GPU, which stalls the pipeline — cheap once, ruinous per frame. The
 * cache is keyed on the vernacular as well as the type, because the same
 * Woodcutter's Camp is a different building in Karrun-Dûr than it is in
 * Emberhold, and `setVernacular` swaps the whole bank under us when the run's
 * people change.
 */

import type { TextureBank } from '../render/textures.ts';

/** `${vernacular}:${typeIdx}` → data URL. */
const cache = new Map<string, string>();

/**
 * A building's picture as a data URL, or '' if the bank is not baked yet.
 *
 * The empty string rather than a throw or a placeholder: this is called while
 * the list is being assembled, and the list is assembled once on the very first
 * frame — before `TextureBank.build` has necessarily run — and again the moment
 * a world arrives. A row with no picture for one frame is invisible; a row that
 * threw would take the HUD with it.
 */
export function buildingThumb(bank: TextureBank | null, typeIdx: number): string {
  if (!bank) return '';
  const tex = bank.buildings[typeIdx];
  if (!tex) return '';
  const key = `${bank.vernacularId}:${typeIdx}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  let url: string;
  try {
    const canvas = bank.renderer.extract.canvas(tex) as HTMLCanvasElement;
    url = canvas.toDataURL('image/png');
  } catch {
    // A readback can fail on a lost context or a headless renderer that has not
    // finished coming up. A menu without pictures still works; one that throws
    // in the middle of a rebuild leaves half a list on the screen. It is not
    // cached on failure, so the next rebuild tries again.
    return '';
  }
  cache.set(key, url);
  return url;
}
